'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Flame, Menu, Sparkles, Sun, Sunset, Moon } from 'lucide-react';
import SidebarMenuContent from './SidebarMenuContent';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLearningStreak } from '@/hooks/useLearningStreak';
import { useLearningXp } from '@/hooks/useLearningXp';
import NotificationModal, { countUnreadNotifications } from './NotificationModal';

interface HeaderProps {
  name: string;
  email: string;
  greet?: boolean;
  title?: string;
}

const Header: React.FC<HeaderProps> = ({ name, greet = false, title, email }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const { streak, loading: streakLoading } = useLearningStreak();
  const { xp, loading: xpLoading } = useLearningXp();
  const [readIds, setReadIds] = useState<string[]>([]);
  const unreadNotifications = (mounted && !streakLoading && !xpLoading) ? countUnreadNotifications(streak, xp, readIds) : 0;

  useEffect(() => {
    try {
      const stored = localStorage.getItem('notificationReadIds');
      if (stored) {
        setReadIds(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to parse notificationReadIds from local storage', e);
    }
  }, []);

  const handleReadNotification = (id: string) => {
    if (!readIds.includes(id)) {
      setReadIds((prev) => {
        const newIds = [...prev, id];
        try {
          localStorage.setItem('notificationReadIds', JSON.stringify(newIds));
        } catch (e) {
          console.error('Failed to save notificationReadIds to local storage', e);
        }
        return newIds;
      });
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
    setIsNotificationOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isNotificationOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isNotificationOpen]);

  useEffect(() => {
    if (!isSidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isSidebarOpen]);

  const time = new Date().getHours();
  let greeting = 'Selamat pagi';
  let TimeIcon = Sun;

  if (time >= 11 && time < 15) {
    greeting = 'Selamat siang';
    TimeIcon = Sun;
  } else if (time >= 15 && time < 18) {
    greeting = 'Selamat sore';
    TimeIcon = Sunset;
  } else if (time >= 18) {
    greeting = 'Selamat malam';
    TimeIcon = Moon;
  }

  useEffect(() => {
    if (name && !localStorage.getItem('name')) {
      localStorage.setItem('name', name);
    }
  }, [name]);

  const getName = (name: string) => {
    console.log('name', name)
    let xname = name
    if (!xname || xname === '') {
      xname = localStorage.getItem('name') ?? ''
    }
    console.log('xname', xname)
    return xname.charAt(0).toUpperCase();
  }

  const getFirstname = (name: string) => {
    const firstname = name?.split(' ')[0];
    return firstname
  }

  const nameInitial = () => {
    const n = name?.trim();
    if (!n) return '?';
    return n.charAt(0).toUpperCase();
  };

  return (
    <>
      <div className="relative flex min-h-[60px] items-center justify-between gap-3 overflow-hidden border border-b-0 border-brand-border bg-white/95 px-4 py-2.5 backdrop-blur lg:rounded-t-lg lg:px-5">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-violet-100/50 blur-3xl"
          aria-hidden
        />

        {/* Left: menu + greeting */}
        <div className="relative flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => setIsSidebarOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-700 transition hover:bg-gray-100 lg:hidden"
            aria-label="Buka menu"
          >
            <Menu size={22} />
          </button>

          {/* <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-100 text-violet-600 shadow-sm ring-1 ring-violet-100 sm:flex">
            <TimeIcon className="h-5 w-5" />
          </span> */}

          <div className="hidden min-w-0 sm:block">
          <h1 className="text-2xl font-bold text-gray-800"><span className="truncate">{greeting}, {getFirstname(name)}</span></h1>
            {/* <p className="truncate text-xs text-gray-500">{email}</p> */}
          </div>
        </div>

        {/* Right: actions */}
        <div className="relative flex shrink-0 items-center gap-2 sm:gap-3">
          {/* <Link
            href="/track"
            className={`hidden sm:flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 transition hover:brightness-105 sm:px-3 sm:py-2 ${streak.todayCompleted
              ? 'border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-sm shadow-orange-100/60'
              : streak.currentStreak > 0
                ? 'border-orange-100 bg-orange-50/80'
                : 'border-gray-200 bg-white'
              }`}
            aria-label={`Streak belajar ${streak.currentStreak} hari`}
            title={
              streak.todayCompleted
                ? 'Streak hari ini sudah tercatat'
                : streak.currentStreak > 0
                  ? 'Belajar hari ini agar streak tidak putus'
                  : 'Mulai streak belajar harianmu'
            }
          >
            <Flame
              className={`h-4 w-4 sm:h-[18px] sm:w-[18px] ${streak.currentStreak > 0 ? 'text-orange-500' : 'text-gray-400'
                }`}
              strokeWidth={2.2}
            />
            <span
              className={`text-sm font-bold tabular-nums ${streak.currentStreak > 0 ? 'text-orange-700' : 'text-gray-500'
                }`}
            >
              {streakLoading ? '—' : streak.currentStreak}
            </span>
            <span className="hidden text-xs font-medium text-orange-600/80 sm:inline">hari</span>
          </Link> */}

          <Link
            href="/track"
            className="hidden sm:flex items-center gap-1.5 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 px-2.5 py-1.5 shadow-sm shadow-violet-100/50 transition hover:brightness-105 sm:px-3 sm:py-2"
            aria-label={`Total XP ${xp.totalXp}`}
            title="Lihat total XP kamu"
          >
            <Sparkles className="h-4 w-4 text-violet-600 sm:h-[18px] sm:w-[18px]" strokeWidth={2.2} />
            <span className="text-sm font-bold tabular-nums text-violet-700">
              {xpLoading ? '—' : xp.totalXp}
            </span>
            <span className="hidden text-xs font-medium text-violet-600/80 sm:inline">XP</span>
          </Link>

          <button
            type="button"
            onClick={() => setIsNotificationOpen(true)}
            className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
            aria-label="Notifikasi"
          >
            <Bell className="h-5 w-5" />
            {mounted && unreadNotifications > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
              </span>
            )}
          </button>

          <Link
            href="/profile"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-purple-200 bg-purple-100 text-sm font-bold font-bold text-purple-700 shadow-sm ring-2 ring-white transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-violet-400"
            aria-label="Profil"
          >
            <span>{nameInitial()}</span>
          </Link>
        </div>
      </div>

      {mounted &&
        createPortal(
          <NotificationModal
            isOpen={isNotificationOpen}
            onClose={() => setIsNotificationOpen(false)}
            streak={streak}
            xp={xp}
            readIds={readIds}
            onRead={handleReadNotification}
          />,
          document.body
        )}

      {mounted &&
        createPortal(
          <>
            <div
              className={`fixed top-0 left-0 z-[9999] flex h-[100dvh] max-h-[100dvh] w-64 flex-col overflow-hidden bg-white transform transition-transform duration-300 lg:hidden ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
              <SidebarMenuContent name={name} email={email} />
            </div>
            <div
              className={`fixed inset-0 bg-black bg-opacity-50 z-[9998] lg:hidden transition-opacity duration-300 ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              onClick={() => setIsSidebarOpen(false)}
            />
          </>,
          document.body
        )}
    </>
  );
};

export default Header;